import { useEffect, useState } from "react";
import api from "../../lib/api";

const Stat = ({ label, value, accent = false }) => (
  <div
    data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`bg-onyx border ${accent ? "border-gold" : "border-white/10"} p-6`}
  >
    <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">
      {label}
    </div>
    <div className="font-heading text-4xl text-ivory">{value ?? "—"}</div>
  </div>
);

const Bar = ({ value, max }) => {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 bg-white/5 w-full">
      <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
    </div>
  );
};

const VisitorAnalytics = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = (windowDays) => {
    setLoading(true);
    api
      .get(`/admin/analytics/summary?days=${windowDays}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
    const t = setInterval(() => load(days), 60_000); // refresh live count every minute
    return () => clearInterval(t);
  }, [days]);

  const maxDay = Math.max(1, ...(data?.by_day || []).map((d) => d.views));
  const maxPage = Math.max(1, ...(data?.top_pages || []).map((p) => p.views));
  const maxRef = Math.max(1, ...(data?.top_referrers || []).map((r) => r.views));

  return (
    <div data-testid="visitor-analytics" className="space-y-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl text-ivory">Visitor Analytics</h2>
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-white/40 mt-2">
            First-party · Anonymous · No cookies required
          </p>
        </div>
        <div className="flex gap-2">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`analytics-days-${d}`}
              onClick={() => setDays(d)}
              className={`font-accent text-[11px] tracking-[0.28em] uppercase px-4 py-2 border ${
                days === d
                  ? "border-gold text-gold"
                  : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {d === 1 ? "24 H" : `${d} D`}
            </button>
          ))}
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat
          label="Live (5m)"
          value={
            loading ? "…" : <span className="text-gold">{data?.live ?? 0}</span>
          }
          accent
        />
        <Stat label="Today views" value={loading ? "…" : data?.today_views} />
        <Stat label="Today unique" value={loading ? "…" : data?.today_unique} />
        <Stat label={`${days}-day views`} value={loading ? "…" : data?.total_views} />
        <Stat label={`${days}-day uniques`} value={loading ? "…" : data?.unique_visitors} />
      </div>

      {/* Day chart */}
      <div className="bg-onyx border border-white/10 p-8">
        <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-6">
          Visits by day
        </div>
        {(data?.by_day || []).length === 0 ? (
          <div className="text-white/40 text-sm">No traffic in this window yet.</div>
        ) : (
          <div className="space-y-3">
            {data.by_day.map((d) => (
              <div key={d.day} className="grid grid-cols-12 items-center gap-3">
                <div className="col-span-3 font-accent text-[10px] tracking-[0.22em] uppercase text-white/50">
                  {d.day}
                </div>
                <div className="col-span-7">
                  <Bar value={d.views} max={maxDay} />
                </div>
                <div className="col-span-2 text-right font-heading text-ivory text-lg">
                  {d.views}{" "}
                  <span className="text-white/40 text-xs">/ {d.unique}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top Pages + Referrers */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-onyx border border-white/10 p-8">
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-6">
            Top pages
          </div>
          {(data?.top_pages || []).length === 0 ? (
            <div className="text-white/40 text-sm">—</div>
          ) : (
            <div className="space-y-3">
              {data.top_pages.map((p) => (
                <div key={p.path} className="grid grid-cols-12 items-center gap-2 text-sm">
                  <div className="col-span-8 truncate text-ivory">{p.path}</div>
                  <div className="col-span-3">
                    <Bar value={p.views} max={maxPage} />
                  </div>
                  <div className="col-span-1 text-right text-gold">{p.views}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-onyx border border-white/10 p-8">
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-6">
            Top referrers
          </div>
          {(data?.top_referrers || []).length === 0 ? (
            <div className="text-white/40 text-sm">No external referrers yet.</div>
          ) : (
            <div className="space-y-3">
              {data.top_referrers.map((r) => (
                <div key={r.host} className="grid grid-cols-12 items-center gap-2 text-sm">
                  <div className="col-span-8 truncate text-ivory">{r.host}</div>
                  <div className="col-span-3">
                    <Bar value={r.views} max={maxRef} />
                  </div>
                  <div className="col-span-1 text-right text-gold">{r.views}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Language distribution */}
      <div className="bg-onyx border border-white/10 p-8">
        <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-6">
          Visitor language
        </div>
        <div className="flex flex-wrap gap-2">
          {(data?.by_lang || []).slice(0, 12).map((l) => (
            <div
              key={l.lang}
              className="px-4 py-2 border border-white/10 text-sm"
            >
              <span className="text-ivory">{l.lang || "—"}</span>{" "}
              <span className="text-gold">{l.views}</span>
            </div>
          ))}
          {(data?.by_lang || []).length === 0 && (
            <div className="text-white/40 text-sm">—</div>
          )}
        </div>
      </div>

      <p className="text-xs text-white/30 italic">
        Tip: This dashboard is fully first-party and works without cookies. For
        deeper attribution (ad spend, conversions, audiences), use your{" "}
        <a
          href="https://business.facebook.com/events_manager2/list/pixel/35193983740216764/overview"
          target="_blank"
          rel="noreferrer"
          className="text-gold underline"
        >
          Meta Events Manager
        </a>{" "}
        or Google Analytics 4 dashboards in parallel.
      </p>
    </div>
  );
};

export default VisitorAnalytics;
